/*
 * Account log in and two-step verification.
 *
 * Drives Craft's own endpoints (users/login, auth/verify-totp,
 * auth/verify-recovery-code, auth/method-setup-html,
 * auth/generate-recovery-codes) from the account templates. Nothing here
 * decides who is allowed in; it only shows Craft's answers in our markup.
 * See .claude/docs/ACCOUNT-AUTH.md before changing it.
 */

const TOTP = 'craft\\auth\\methods\\TOTP';
const RECOVERY = 'craft\\auth\\methods\\RecoveryCodes';
const GENERIC_ERROR = 'Sorry, something went wrong. Please try again.';

function initAuth(root) {
	const csrf = { name: root.dataset.csrfName, value: root.dataset.csrfValue };
	const errorBox = root.querySelector('[data-auth-error]');
	const passwordStep = root.querySelector('[data-auth-password]');
	const verifyStep = root.querySelector('[data-auth-verify]');
	const loginForm = root.querySelector('[data-auth-login]');
	const heading = document.querySelector('[data-auth-heading]');

	// What to do once the visitor is logged in; the security page swaps this
	// while an authenticator app is being set up.
	let onLoggedIn = function(returnUrl) {
		window.location.assign(root.dataset.returnUrl || returnUrl || '/account/your-account');
	};

	async function post(action, params) {
		const body = new URLSearchParams(params || {});
		body.set(csrf.name, csrf.value);

		const response = await fetch('/actions/' + action, {
			method: 'POST',
			headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
			credentials: 'same-origin',
			body: body,
		});

		let data = {};
		try {
			data = await response.json();
		} catch (e) {
			// Not JSON (an error page); the status is enough
		}

		if (data.csrfTokenValue) {
			csrf.value = data.csrfTokenValue;
		}

		return { ok: response.ok, status: response.status, data: data };
	}

	function showError(message) {
		errorBox.textContent = message || GENERIC_ERROR;
		errorBox.hidden = false;
		errorBox.scrollIntoView({ block: 'nearest' });
	}

	function clearError() {
		errorBox.hidden = true;
		errorBox.textContent = '';
	}

	function setBusy(form, busy) {
		const button = form.querySelector('button[type="submit"]');
		if (!button) {
			return;
		}
		button.disabled = busy;
		button.querySelector('span').classList.toggle('hidden', busy);
		button.querySelector('svg').classList.toggle('hidden', !busy);
	}

	// Runs a form's request with the busy state and error handling around it
	async function submit(form, request) {
		clearError();
		setBusy(form, true);
		try {
			return await request();
		} catch (e) {
			showError();
			return null;
		} finally {
			setBusy(form, false);
		}
	}

	/* Step 1: password ---------------------------------------------------- */

	if (loginForm) {
		loginForm.addEventListener('submit', async function(e) {
			e.preventDefault();
			e.stopPropagation(); // keep the site-wide submit spinner (form.js) out of it

			const fields = new FormData(loginForm);
			const result = await submit(loginForm, function() {
				return post('users/login', {
					loginName: fields.get('loginName'),
					password: fields.get('password'),
					rememberMe: fields.get('rememberMe') || '',
				});
			});

			if (!result) {
				return;
			}

			if (result.data.authMethod) {
				showVerifyStep(result.data);
			} else if (result.ok) {
				onLoggedIn(result.data.returnUrl);
			} else {
				showError(result.data.message);
				loginForm.querySelector('[name="password"]').value = '';
				loginForm.querySelector('[name="password"]').focus();
			}
		});
	}

	/* Step 2: verification code ------------------------------------------- */

	let pendingReturnUrl = null;

	function setVerifyMethod(method) {
		verifyStep.dataset.method = method;
		verifyStep.querySelectorAll('[data-auth-method]').forEach(function(panel) {
			panel.hidden = panel.dataset.authMethod !== method;
		});
		const switcher = verifyStep.querySelector('[data-auth-switch]');
		switcher.textContent = method === 'totp' ? 'Use a recovery code instead' : 'Use your authenticator app instead';
		verifyStep.querySelector('[data-auth-method="' + method + '"] input').focus();
	}

	function showVerifyStep(data) {
		const methods = [data.authMethod].concat((data.otherMethods || []).map(function(m) { return m.class; }));
		pendingReturnUrl = data.returnUrl;

		passwordStep.hidden = true;
		verifyStep.hidden = false;
		verifyStep.querySelector('[data-auth-switch]').hidden = !(methods.includes(TOTP) && methods.includes(RECOVERY));
		if (heading && root.dataset.verifyHeading) {
			heading.textContent = root.dataset.verifyHeading;
		}
		setVerifyMethod(methods.includes(TOTP) ? 'totp' : 'recovery');
	}

	if (verifyStep) {
		const verifyForm = verifyStep.querySelector('[data-auth-verify-form]');

		verifyStep.querySelector('[data-auth-switch]').addEventListener('click', function() {
			clearError();
			setVerifyMethod(verifyStep.dataset.method === 'totp' ? 'recovery' : 'totp');
		});

		verifyForm.addEventListener('submit', async function(e) {
			e.preventDefault();
			e.stopPropagation();

			const recovery = verifyStep.dataset.method === 'recovery';
			const input = verifyStep.querySelector('[data-auth-method="' + verifyStep.dataset.method + '"] input');
			const code = input.value.trim();
			if (!code) {
				input.focus();
				return;
			}

			const result = await submit(verifyForm, function() {
				return post(recovery ? 'auth/verify-recovery-code' : 'auth/verify-totp', { code: code });
			});

			if (!result) {
				return;
			}

			if (result.ok) {
				onLoggedIn(pendingReturnUrl);
			} else {
				showError(result.data.message || 'That code didn\'t work. Check it and try again.');
				input.value = '';
				input.focus();
			}
		});
	}

	/* Security page: authenticator app and recovery codes ------------------ */

	if (!root.hasAttribute('data-auth-security')) {
		return;
	}

	const confirmBox = root.querySelector('[data-auth-confirm]');
	const actions = root.querySelector('[data-auth-actions]');
	const codesBox = root.querySelector('[data-recovery-codes]');

	// Craft only allows these changes shortly after a login ("elevated
	// session"). When that has lapsed, ask for the password again.
	function askForPassword(message, then) {
		if (actions) {
			actions.hidden = true;
		}
		confirmBox.hidden = false;
		confirmBox.querySelector('[data-auth-confirm-intro]').textContent = message;
		confirmBox.querySelector('[name="password"]').focus();
		if (then) {
			onLoggedIn = then;
		}
	}

	function showRecoveryCodes(codes) {
		const list = codesBox.querySelector('[data-recovery-list]');
		list.replaceChildren.apply(list, codes.map(function(code) {
			const item = document.createElement('li');
			item.textContent = code;
			return item;
		}));
		if (actions) {
			actions.hidden = true;
		}
		codesBox.hidden = false;
		codesBox.querySelector('[data-recovery-heading]').focus();
	}

	async function generateRecoveryCodes() {
		const result = await post('auth/generate-recovery-codes');
		if (result.ok && result.data.codes) {
			showRecoveryCodes(result.data.codes);
		} else if (result.status === 403) {
			askForPassword('For your security we need your password again before showing new recovery codes.');
		} else {
			showError(result.data.message);
		}
	}

	// Set up an authenticator app
	const startButton = root.querySelector('[data-totp-start]');
	const setupPanel = root.querySelector('[data-totp-setup]');

	if (startButton && setupPanel) {
		const setupForm = setupPanel.querySelector('[data-totp-form]');
		const codeInput = setupForm.querySelector('input');

		startButton.addEventListener('click', async function() {
			clearError();
			startButton.disabled = true;

			let result = null;
			try {
				result = await post('auth/method-setup-html', { method: TOTP });
			} catch (e) {
				// handled below
			}

			// Craft returns control-panel markup; we only want the QR code and key
			const doc = result && result.ok ? new DOMParser().parseFromString(result.data.html || '', 'text/html') : null;
			const qr = doc && doc.querySelector('[id$="qr-code-wrapper"] svg');
			const key = doc && doc.querySelector('input[name*="setup-key"]');

			if (!qr || !key) {
				startButton.disabled = false;
				showError(result && result.data.message);
				return;
			}

			qr.setAttribute('role', 'img');
			qr.setAttribute('aria-label', 'QR code for your authenticator app');
			setupPanel.querySelector('[data-totp-qr]').replaceChildren(document.importNode(qr, true));
			setupPanel.querySelector('[data-totp-key]').textContent = key.value;

			startButton.classList.add('hidden');
			setupPanel.hidden = false;
			setupPanel.scrollIntoView({ block: 'nearest' });
		});

		setupForm.addEventListener('submit', async function(e) {
			e.preventDefault();
			e.stopPropagation();

			const code = codeInput.value.trim();
			if (!code) {
				codeInput.focus();
				return;
			}

			const result = await submit(setupForm, function() {
				return post('auth/verify-totp', { code: code });
			});

			if (!result) {
				return;
			}

			if (result.ok) {
				root.querySelectorAll('[data-totp-on]').forEach(function(el) { el.hidden = false; });
				root.querySelectorAll('[data-totp-off]').forEach(function(el) { el.hidden = true; });
				await generateRecoveryCodes();
			} else if (result.status === 403) {
				// The app is already set up on their phone; keep the same key
				// and just get a fresh login, then a fresh code.
				codeInput.value = '';
				askForPassword('That took a little while, so for your security we need your password again. Your authenticator app is still set up correctly.', function() {
					confirmBox.hidden = true;
					actions.hidden = false;
					clearError();
					codeInput.focus();
				});
			} else {
				showError(result.data.message || 'That code didn\'t work. Check it and try again.');
				codeInput.value = '';
				codeInput.focus();
			}
		});
	}

	// Generate new recovery codes
	const generateButton = root.querySelector('[data-recovery-generate]');
	if (generateButton) {
		generateButton.addEventListener('click', async function() {
			if (generateButton.dataset.confirm && !window.confirm(generateButton.dataset.confirm)) {
				return;
			}
			clearError();
			generateButton.disabled = true;
			try {
				await generateRecoveryCodes();
			} catch (e) {
				showError();
			}
			generateButton.disabled = false;
		});
	}

	const copyButton = root.querySelector('[data-recovery-copy]');
	if (copyButton) {
		copyButton.addEventListener('click', async function() {
			const codes = Array.from(codesBox.querySelectorAll('[data-recovery-list] li')).map(function(li) { return li.textContent; });
			const label = copyButton.querySelector('span');
			try {
				await navigator.clipboard.writeText(codes.join('\n'));
				label.textContent = 'Copied';
			} catch (e) {
				label.textContent = 'Couldn\'t copy';
			}
		});
	}

	// The download is a normal form post to Craft. Keep form.js's page-wide
	// spinner out of it (nothing navigates), and check the elevated session
	// first so a lapsed one asks for the password instead of a 403 page.
	root.querySelectorAll('[data-recovery-download]').forEach(function(form) {
		form.addEventListener('submit', async function(e) {
			e.preventDefault();
			e.stopPropagation();
			clearError();

			let timeout = 0;
			try {
				const response = await fetch('/actions/users/get-elevated-session-timeout', {
					headers: { 'Accept': 'application/json' },
					credentials: 'same-origin',
				});
				timeout = (await response.json()).timeout;
			} catch (e) {
				// treat as lapsed
			}

			// false means elevated sessions are switched off; a number is seconds left
			if (timeout === false || timeout > 0) {
				form.submit();
			} else {
				askForPassword('For your security we need your password again before downloading your recovery codes.');
			}
		});
	});
}

document.querySelectorAll('[data-auth]').forEach(initAuth);
